/** @file
 *  Provides functions for filtering ebab portfolio data.
 */
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
    FILTER_IDS,
    PEER_FILTER_ACTIONS
} from '../components/contexts/filterStates';
import { STATUS } from './visibility';

/**
 * Filter ebabData array using the currently set peer filters state. Uses logical AND when multiple filter ids are set,
 * logical OR when multiple attributes specified for one filter id. Also re-ranks the filtered array. Assumes ebabData pre-ranked.
 *
 * @param {Array<Object>} ebabData sorted EBAB data containing portfolio attributes, scores etc.
 * @param {Object} peerFilterStates current state of peer filters
 * @returns {Array<Object>} re-ranked, filtered EBAB data array
 */
export function filterDataByPeerFilters(ebabData, peerFilterStates) {
    return ebabData
        .filter((e) =>
            Object.entries(peerFilterStates.filters).every(
                ([filterId, filterValue]) =>
                    filterValue.value.includes(e[filterId]) ||
                    filterValue.value.length === 0
            )
        )
        .map((e, i) => {
            return { ...e, rank: i + 1 };
        });
}

const muiSearchFilterExpression = createFilterOptions();

/**
 * Filter ebabData array by the portfolioLongName attribute using the currently set search state.
 * Uses the MUI Autocomplete filtering expression, and also filters out hidden portfolios. Does not re-rank the array.
 *
 * @param {Array<Object>} ebabData sorted EBAB data containing portfolio attributes, scores etc.
 * @param {string} searchState the string being searched for
 * @returns {Array<Object>} filtered EBAB data array
 */
export function filterDataByPortfolioLongName(ebabData, searchState) {
    let tempEbabData = [...ebabData];

    if (searchState) {
        tempEbabData = muiSearchFilterExpression(
            tempEbabData.filter((p) => p.status !== STATUS.hidden),
            {
                inputValue: searchState,
                getOptionLabel: (option) => option.portfolioLongName
            }
        );
    }
    return tempEbabData;
}

/**
 * Helper function which finds distinct, non-null portfolio attributes from EBAB data for given filter id,
 * to be used as the dropdown filter options. Sorts these alphabetically.
 *
 * @param {Array<Object>} ebabData full EBAB data array
 * @param {string} filterId ID of filter - corresponding to key in EBAB data we are filtering by (e.g portfolio_style)
 * @returns {Array<string>} array of distinct values found for this filter ID (non-null)
 */
function getPeerFilterOptions(ebabData, filterId) {
    return [...new Set(ebabData.map((e) => e[filterId]))]
        .filter((i) => i != null)
        .sort((a, b) => a.localeCompare(b));
}

/**
 * Reducer function which takes the current peer filter state and an action, and returns the new peer filter state.
 * Currently there are three possible actions, details of each documented below.
 *
 * @param {Object} peerFilterStates the current peer filter state object
 * @param {Object} peerFilterAction the action to be performed on the state object, has a type and payload
 * @returns {Object} the new state object after action has been applied
 */
export function peerFilterReducer(peerFilterStates, peerFilterAction) {
    switch (peerFilterAction.type) {
        /**
         * For each filter id get the possible options from the current EBAB data array to be used in the peer filter dropdowns.
         * In this case, payload is the full EBAB data array.
         */
        case PEER_FILTER_ACTIONS.SET_OPTIONS:
            return {
                filters: Object.assign(
                    {},
                    ...Object.entries(peerFilterStates.filters).map(
                        ([k, v]) => {
                            return {
                                [k]: {
                                    ...v,
                                    options: getPeerFilterOptions(
                                        peerFilterAction.payload,
                                        k
                                    )
                                }
                            };
                        }
                    )
                ),
                optionsUpdated: true
            };
        /**
         * For an array of given filter IDs and values, set the state of these filters to the provided values.
         * The id of each payload item correspond to the filter id, and the values are an array of desired values to filter by.
         */
        case PEER_FILTER_ACTIONS.SET_FILTERS: {
            let newState = {};

            for (let filter of peerFilterAction.payload) {
                newState[filter.id] = {
                    ...peerFilterStates.filters[filter.id],
                    value: filter.value
                };
            }
            return {
                filters: { ...peerFilterStates.filters, ...newState },
                optionsUpdated: false
            };
        }
        /**
         * Clears all currently set peer filters
         */
        case PEER_FILTER_ACTIONS.CLEAR_FILTERS:
            return {
                filters: Object.assign(
                    {},
                    ...Object.entries(peerFilterStates.filters).map(
                        ([k, v]) => {
                            return { [k]: { ...v, value: [] } };
                        }
                    )
                ),
                optionsUpdated: false
            };
        default:
            return peerFilterStates;
    }
}

/**
 * Returns whether any peer filters or search terms are currently set
 *
 * @param {Object} filterState the current filter state object
 * @returns {boolean} True if any filter or search term is set, false otherwise
 */
export function isAnyFilterSet(filterState) {
    return (
        filterState.searchState ||
        Object.values(filterState.peerFilterStates.filters).some(
            (i) => i.value.length > 0
        )
    );
}

/**
 * Function to set peer filters for the current dot's corresponding geo category, style, and market cap focus values.
 * If any of these attributes are null, they will be removed from the filter payload.
 * @param {Object} dotData Object containing the dot's data (e.g score, name, filter attributes)
 * @param {Function} filterDispatch Dispatch function for updating filterStates state
 */
export function setComparisonFilters(dotData, filterDispatch) {
    const filtersPayload = [
        { id: FILTER_IDS.GEOGRAPHY, value: [dotData.geoCategory] },
        { id: FILTER_IDS.MARKET_CAP, value: [dotData.marketCapFocus] },
        { id: FILTER_IDS.STYLE, value: [dotData.style] }
    ].filter((f) => f.value[0] != null);

    filterDispatch({
        type: PEER_FILTER_ACTIONS.SET_FILTERS,
        payload: filtersPayload
    });
}
